import React, { useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import Grid from '../common/Grid';
import {
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Science as ScienceIcon,
  Assignment as AssignmentIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';
import {
  getSaltComposition,
  getFormulationTargets,
  saltHasComposition,
} from '../../utils/saltElements';

interface Salt {
  id: string;
  name: string;
  formula: string;
  elements?: any[];
}

interface Formulation {
  name: string;
  elements?: any[];
  targetElements?: any[];
}

interface SelectionAnalysisPanelProps {
  selectedSalts: Salt[];
  formulation?: Formulation;
  onConfirmSelection: () => void;
  onBackToFormulation: () => void;
  canProceed: boolean;
}

const SelectionAnalysisPanel: React.FC<SelectionAnalysisPanelProps> = ({
  selectedSalts,
  formulation,
  onConfirmSelection,
  onBackToFormulation,
  canProceed,
}) => {
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  const incompleteSalts = useMemo(
    () => selectedSalts.filter((s) => !saltHasComposition(s)),
    [selectedSalts]
  );

  const completeSalts = useMemo(
    () => selectedSalts.filter((s) => saltHasComposition(s)),
    [selectedSalts]
  );

  /** Per nutrient: which complete salts supply it (by normalized symbol). */
  const supplyMap = useMemo(() => {
    const map = new Map<
      string,
      { display: string; sources: { salt: string; rawSymbol: string; percentage: number }[] }
    >();

    completeSalts.forEach((salt) => {
      getSaltComposition(salt).forEach((el) => {
        const key = el.normalized;
        if (!map.has(key)) {
          map.set(key, { display: el.normalized, sources: [] });
        }
        map.get(key)!.sources.push({
          salt: salt.name,
          rawSymbol: el.rawSymbol,
          percentage: el.percentage,
        });
      });
    });

    return map;
  }, [completeSalts]);

  const targets = useMemo(() => getFormulationTargets(formulation), [formulation]);

  const coverageRows = useMemo(() => {
    if (!targets.length) return [];

    // Unique by normalized target — N, N_NO3, N_NH4 are distinct chemical channels
    const seen = new Set<string>();
    const rows: {
      key: string;
      label: string;
      concentration?: number;
      covered: boolean;
      sources: { salt: string; rawSymbol: string; percentage: number }[];
    }[] = [];

    for (const t of targets) {
      if (seen.has(t.normalized)) continue;
      seen.add(t.normalized);

      // Exact channel first
      let sources = supplyMap.get(t.normalized)?.sources || [];

      // Total N target (N) is covered by N_NO3 and/or N_NH4 and/or plain N salts
      // (N means NH4+NO3 — never treat as only one form)
      if (t.normalized === 'N' && sources.length === 0) {
        const combined = [
          ...(supplyMap.get('N_NO3')?.sources || []),
          ...(supplyMap.get('N_NH4')?.sources || []),
          ...(supplyMap.get('N')?.sources || []),
        ];
        sources = combined;
      }

      // Form targets are never satisfied by the other N form
      const covered = sources.length > 0;
      const label =
        t.normalized === 'N'
          ? 'N (total = NH₄-N + NO₃-N)'
          : t.normalized === 'N_NO3'
            ? 'N_NO3 (nitrate-N only)'
            : t.normalized === 'N_NH4'
              ? 'N_NH4 (ammonium-N only)'
              : t.symbol !== t.normalized
                ? `${t.normalized} (as ${t.symbol})`
                : t.normalized;

      rows.push({
        key: t.normalized,
        label,
        concentration: Number.isFinite(t.concentration) ? t.concentration : undefined,
        covered,
        sources,
      });
    }
    return rows;
  }, [targets, supplyMap]);

  const covered = coverageRows.filter((r) => r.covered);
  const missing = coverageRows.filter((r) => !r.covered);

  /** Extra nutrients supplied that are not in formulation targets */
  const extras = useMemo(() => {
    const targetNorm = new Set(coverageRows.map((r) => r.key));
    return [...supplyMap.entries()]
      .filter(([k]) => !targetNorm.has(k))
      .map(([k, v]) => ({
        key: k,
        sources: v.sources,
      }));
  }, [supplyMap, coverageRows]);

  const hasIncompatible = false;
  // Full coverage is ideal; still allow proceed with complete salts + warning
  const canConfirm =
    completeSalts.length > 0 &&
    incompleteSalts.length === 0 &&
    !hasIncompatible;
  const fullCoverage = targets.length === 0 || missing.length === 0;

  const handleConfirmClick = () => setConfirmationOpen(true);
  const handleConfirmProceed = () => {
    setConfirmationOpen(false);
    // Call after dialog state update so parent navigate is not lost
    window.setTimeout(() => {
      onConfirmSelection();
    }, 0);
  };

  return (
    <>
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Selection Analysis & Confirmation
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }} icon={<HelpIcon />}>
          <Typography variant="body2">
            <strong>Coverage</strong> means: for each nutrient in your formulation, at least one
            selected salt lists that element in its composition (e.g. Zn% in Zinc Sulfate).
            It does <em>not</em> mean the dose is correct yet — Stage 4–5 calculate amounts.
            <br />
            Prefer salts that show element % (e.g. “Zn: 36%”). Rows marked{' '}
            <strong>Not entered</strong> cannot cover anything — delete or fix them.
            N_NO₃ / N_NH₄ count as <strong>N</strong> for matching.
          </Typography>
        </Alert>

        {incompleteSalts.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Incomplete composition — cannot use for coverage
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              {incompleteSalts.map((s) => (
                <Chip key={s.id} label={s.name} color="error" size="small" />
              ))}
            </Box>
            <Typography variant="body2">
              These salts have no element percentages in the database (“Not entered”). Uncheck them
              and select the twin that shows % composition (e.g. Zinc Sulfate Monohydrate with Zn
              and S listed — not an empty “Zinc Sulfate”).
            </Typography>
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid xs={12} md={5}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Selected for this workflow ({selectedSalts.length})
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedSalts.map((salt) => {
                    const ok = saltHasComposition(salt);
                    return (
                      <Chip
                        key={salt.id}
                        label={salt.name}
                        size="small"
                        color={ok ? 'primary' : 'error'}
                        variant={ok ? 'outlined' : 'filled'}
                        icon={ok ? <CheckIcon /> : <ErrorIcon />}
                      />
                    );
                  })}
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Green outline = composition loaded · Red = incomplete (Not entered)
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid xs={12} md={7}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Formulation coverage
                  {formulation?.name ? (
                    <Typography component="span" variant="body2" color="text.secondary">
                      {' '}
                      — {formulation.name}
                    </Typography>
                  ) : null}
                </Typography>

                {!targets.length ? (
                  <Typography variant="body2" color="text.secondary">
                    {formulation
                      ? 'This formulation has no target elements defined. You can still proceed, but coverage cannot be checked.'
                      : 'No formulation in workflow yet. Complete Stage 2 (Formulation) first for coverage analysis.'}
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Target nutrient</TableCell>
                          <TableCell>Target (ppm)</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Supplied by (salt · % in salt)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {coverageRows.map((row) => (
                          <TableRow key={row.key}>
                            <TableCell>
                              <strong>{row.key}</strong>
                              {row.label !== row.key && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  {row.label}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {row.concentration != null && !Number.isNaN(row.concentration)
                                ? row.concentration
                                : '—'}
                            </TableCell>
                            <TableCell>
                              {row.covered ? (
                                <Chip
                                  size="small"
                                  color="success"
                                  icon={<CheckIcon />}
                                  label="Covered"
                                />
                              ) : (
                                <Chip
                                  size="small"
                                  color="error"
                                  icon={<ErrorIcon />}
                                  label="No source"
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              {row.sources.length === 0 ? (
                                <Typography variant="caption" color="error">
                                  Select a salt that contains {row.key}
                                </Typography>
                              ) : (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {row.sources.map((src, i) => (
                                    <Chip
                                      key={`${src.salt}-${i}`}
                                      size="small"
                                      variant="outlined"
                                      label={`${src.salt}: ${src.percentage}% ${
                                        src.rawSymbol !== row.key ? `(${src.rawSymbol})` : ''
                                      }`}
                                    />
                                  ))}
                                </Box>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {targets.length > 0 && (
                  <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      size="small"
                      color="success"
                      label={`${covered.length} covered`}
                    />
                    <Chip
                      size="small"
                      color={missing.length ? 'error' : 'default'}
                      label={`${missing.length} missing source`}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ mt: 2 }}>
          <Accordion defaultExpanded={completeSalts.length > 0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">
                What each selected salt provides
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {completeSalts.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No complete salts selected.
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Salt</TableCell>
                        <TableCell>Formula</TableCell>
                        <TableCell>Composition</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {completeSalts.map((salt) => (
                        <TableRow key={salt.id}>
                          <TableCell>{salt.name}</TableCell>
                          <TableCell>
                            <Typography fontFamily="monospace" variant="body2">
                              {salt.formula || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {getSaltComposition(salt).map((el, i) => (
                                <Chip
                                  key={i}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  label={`${el.rawSymbol}: ${el.percentage}%`}
                                />
                              ))}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {extras.length > 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Also supplied (not required by formulation targets):{' '}
                    {extras.map((e) => e.key).join(', ')}
                  </Typography>
                </Alert>
              )}
            </AccordionDetails>
          </Accordion>
        </Box>

        {missing.length > 0 && targets.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Still need a source for:</strong> {missing.map((m) => m.key).join(', ')}.
              Add salts that list those elements (prefer entries with % shown, not “Not entered”).
            </Typography>
          </Alert>
        )}

        {canConfirm && fullCoverage && (
          <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Ready:</strong> every formulation target has at least one complete salt
              source. Next stage will calculate how much of each salt to use.
            </Typography>
          </Alert>
        )}
        {canConfirm && !fullCoverage && targets.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              You can still proceed — some targets lack a salt source (
              {missing.map((m) => m.key).join(', ')}). Stage 4 may under-supply those nutrients.
            </Typography>
          </Alert>
        )}

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 3,
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Button variant="outlined" startIcon={<BackIcon />} onClick={onBackToFormulation}>
            Back to Formulation
          </Button>

          <Button
            variant="contained"
            endIcon={<NextIcon />}
            onClick={handleConfirmClick}
            disabled={!canConfirm || !canProceed}
            size="large"
          >
            Confirm & Proceed to Stock Solutions
          </Button>
        </Box>
        {!canConfirm && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Button enables when at least one complete salt is selected and no incomplete
            (“Not entered”) salts remain checked.
          </Typography>
        )}
      </Paper>

      <Dialog
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssignmentIcon />
            Confirm salt selection
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            {completeSalts.length} complete salt(s) will be used in stock calculations:
          </Typography>
          <List dense>
            {completeSalts.map((salt) => (
              <ListItem key={salt.id}>
                <ListItemIcon>
                  <ScienceIcon />
                </ListItemIcon>
                <ListItemText
                  primary={salt.name}
                  secondary={
                    getSaltComposition(salt)
                      .map((e) => `${e.rawSymbol} ${e.percentage}%`)
                      .join(' · ') || salt.formula
                  }
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmationOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmProceed}>
            Confirm & Proceed
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SelectionAnalysisPanel;
